import { useState, useRef, useEffect } from 'react'

import _ from 'lodash'

import { useStableCallback } from 'core/hooks'

const useHasRecentlyChanged = (time = 400) => {
  const [recentlyChanged, setRecentlyChanged] = useState(false)
  const ref = useRef()
  const onRecentChange = () => {
    setRecentlyChanged(true)
    clearTimeout(ref.current)
    ref.current = _.delay(() => setRecentlyChanged(false), time)
  }
  useEffect(() => () => clearTimeout(ref.current), [])

  return { recentlyChanged, onRecentChange }
}

const useThrottleFunction = (fn) => {
  const stableFn = useStableCallback(fn)
  const options = { leading: false, trailing: true, maxWait: 400 }
  const ref = useRef(_.debounce(stableFn, 150, options))
  return ref.current
}

const useAsyncOnChange = ({ value = '', onChange = (_value) => {} } = {}) => {
  const [localValue, setLocalValue] = useState(value)

  const { recentlyChanged, onRecentChange } = useHasRecentlyChanged(250)

  const throttledOnChange = useThrottleFunction(onChange)

  const handleChange = (value) => {
    setLocalValue(value)
    onRecentChange()
    throttledOnChange(value)
  }

  const isChanged = localValue !== value

  useEffect(() => {
    if (!recentlyChanged) {
      setLocalValue(value)
    }
    // if the the local value hasn't changed (via onChange) for a while,
    // and then the passed value changes, set the local
  }, [value]) // eslint-disable-line

  return {
    value: localValue,
    onChange: handleChange,
    isChanged,
    isFocused: recentlyChanged,
  }
}

export default useAsyncOnChange
